import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDate, formatMoney, istanbulDayString, t } from '@aquabox/shared';
import { useOrder, useOrders, useRegions, useVehicles } from '../api/queries';
import { Icon } from '../components/Icon';
import {
  AssignReasonBadge,
  ChannelPill,
  OrderStatusCell,
  PanelStagePill,
  RegionMatchBadge,
} from '../components/Pills';
import { OrderDetail } from './OrderDetail';
import { NewOrderModal } from './NewOrderModal';
import { BulkAssignDialog } from '../components/BulkAssignDialog';
import { itemsSummary } from '../lib/labels';
import {
  plateAndPerson,
  resolveVehicleLabel,
  usePanelLabelLookup,
  type PanelLabelLookup,
} from '../lib/vehicle-label';
import { periodLabel, type PeriodKey } from '../lib/periods';
import { orderNoteSummary } from '../lib/panel-fields';
import { orderAmount } from '../lib/order-amount';
import { useColumnWidths } from '../components/useColumnWidths';
import type { Order, OrderChannel, OrderStatus } from '../api/types';
import { useShadowAssign, type ShadowRow } from '../api/shadow-assign';

/** `orders.view` localStorage anahtarı: Sade/Gelişmiş seçimi kalıcıdır. Okuma/yazma try/catch
 *  içinde — gizli modda ya da depolama kotası doluyken ekran yine render olmalı (kural: ekran
 *  kırılmaz). */
const ORDERS_VIEW_KEY = 'orders.view';
type OrdersView = 'simple' | 'advanced';

function readStoredView(): OrdersView {
  try {
    return localStorage.getItem(ORDERS_VIEW_KEY) === 'advanced' ? 'advanced' : 'simple';
  } catch {
    return 'simple';
  }
}

function storeView(view: OrdersView): void {
  try {
    localStorage.setItem(ORDERS_VIEW_KEY, view);
  } catch {
    // yoksay — görünüm bu oturumda hâlâ değişir, yalnız kalıcı olmaz.
  }
}

/**
 * Kaynak süzgeci (29.09.2026): patron "Hayat'tan gelenleri göster" demek istiyor; kanal süzgeci
 * müşterinin NEREDEN verdiğini gösterir (Trendyol vs telefon), o işin burası değil.
 *
 * Çipler: Tümü · Hayat · Abant · Telefon · Diğer. "Telefon" elle girilen tüm siparişleri
 * (PHONE + WHATSAPP, kaynak = MANUAL) yakalar; panel tarafında ayrı tutmak ayrı bir çipler
 * satırı daha demek ve telefon/WhatsApp ayrımı kanal süzgecinin işi. "Diğer" panelden çekilen
 * diğer kaynakları yakalar (şu an yalnız GETIR_SU).
 *
 * Kalıcılık: `orders.source` anahtarı; okuma/yazma try/catch — `orders.view` ile aynı gerekçe
 * (gizli mod/kota doluyken ekran kırılmamalı). Boş string = Tümü.
 */
type SourceFilter = 'all' | 'HAYAT_PANEL' | 'ABANT_PANEL' | 'MANUAL' | 'GETIR_SU';
const ORDERS_SOURCE_KEY = 'orders.source';
const SOURCE_CHIPS: { value: SourceFilter; labelKey: Parameters<typeof t>[0]; testId: string }[] = [
  { value: 'all', labelKey: 'panel.filterAll', testId: 'orders-source-all' },
  { value: 'HAYAT_PANEL', labelKey: 'srcLetter.chip.HAYAT_PANEL', testId: 'orders-source-hayat' },
  { value: 'ABANT_PANEL', labelKey: 'srcLetter.chip.ABANT_PANEL', testId: 'orders-source-abant' },
  { value: 'MANUAL', labelKey: 'srcLetter.chip.PHONE', testId: 'orders-source-phone' },
  { value: 'GETIR_SU', labelKey: 'srcLetter.chip.GETIR_SU', testId: 'orders-source-getir' },
];

function readStoredSource(): SourceFilter {
  try {
    const raw = localStorage.getItem(ORDERS_SOURCE_KEY);
    if (raw === 'HAYAT_PANEL' || raw === 'ABANT_PANEL' || raw === 'MANUAL' || raw === 'GETIR_SU') {
      return raw;
    }
    return 'all';
  } catch {
    return 'all';
  }
}

function storeSource(value: SourceFilter): void {
  try {
    if (value === 'all') localStorage.removeItem(ORDERS_SOURCE_KEY);
    else localStorage.setItem(ORDERS_SOURCE_KEY, value);
  } catch {
    // yoksay — süzgeç bu oturumda hâlâ çalışır, yalnız kalıcı olmaz.
  }
}

/**
 * Süzgeç KANALA göre (ENTEGRASYON.md §3): patron "Trendyol'dan gelenleri göster" der,
 * "Hayat panelinden çektiklerimizi göster" demez. Sıra prototipteki sırayı korur.
 */
const CHANNELS: OrderChannel[] = [
  'GETIR_SU',
  'TRENDYOL',
  'YEMEKSEPETI',
  'PHONE',
  'WHATSAPP',
  'BRAND_APP',
];
const DATE_FILTERS: { value: string; period: PeriodKey }[] = [
  { value: 'today', period: 'today' },
  { value: 'yesterday', period: 'yesterday' },
  { value: 'week', period: 'thisWeek' },
  { value: 'all', period: 'all' },
  { value: 'custom', period: 'custom' },
];
const STATUS_TABS: (OrderStatus | 'ALL')[] = [
  'ALL',
  'NEW',
  'CONFIRMED',
  'ASSIGNED',
  'ON_ROUTE',
  'DELIVERED',
  'CANCELLED',
];

/** Siparişler ekranı (§3.6): sekmeler, filtreler, sıralanabilir tablo, sağda detay paneli. */
export function Orders({ canWrite }: { canWrite: boolean }) {
  const [status, setStatus] = useState<OrderStatus | 'ALL'>('ALL');
  const [channels, setChannels] = useState<OrderChannel[]>([]);
  const [vehicleId, setVehicleId] = useState('');
  const [assignment, setAssignment] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'receivedAt' | 'total' | 'status'>('receivedAt');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [params] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Deep-link: ?order=<id> syncs selection when URL changes while on the page
  useEffect(() => {
    const id = params.get('order');
    if (id) setSelectedId(id);
  }, [params]);

  const [view, setView] = useState<OrdersView>(readStoredView);
  // Column resizing — tek hook, anahtar değişince otomatik yeniden okur.
  const { style, headerRef, resizer } = useColumnWidths(
    `orders-${view}`,
    view === 'simple' ? 7 : 9,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>(readStoredSource);
  const [groupByRegion, setGroupByRegion] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  // Dialog'a giden id'ler AÇILIŞTA sabitlenir: seçim yolu → görünen seçili satırlar, bölge
  // düğmesi → o grubun satırları. Global seçim dialog'a doğrudan verilmez.
  const [bulkOrderIds, setBulkOrderIds] = useState<string[]>([]);
  const [suggestedVehicleId, setSuggestedVehicleId] = useState<string | null>(null);
  const regions = useRegions();
  // Kapanışta odak tetikleyiciye döner; tetikleyici DOM'dan kalktıysa liste kartına.
  const bulkTriggerRef = useRef<HTMLElement | null>(null);
  const restoreFocusRef = useRef(false);
  const listRef = useRef<HTMLDivElement>(null);

  function openBulkAssign(ids: string[], suggested: string | null, trigger: HTMLElement): void {
    if (ids.length === 0) return;
    bulkTriggerRef.current = trigger;
    setBulkOrderIds(ids);
    setSuggestedVehicleId(suggested);
    setBulkAssignOpen(true);
  }

  // Dialog kapanınca seçim temizlenir (eski davranış) — ikisi aynı render'da uygulanır ki odak
  // geri verilirken seçim düğmesinin DOM'dan kalkıp kalkmadığı kesin bilinsin.
  function closeBulkAssign(): void {
    restoreFocusRef.current = true;
    setBulkAssignOpen(false);
    setBulkOrderIds([]);
    setSelectedOrderIds(new Set());
  }

  useEffect(() => {
    if (bulkAssignOpen || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    const trigger = bulkTriggerRef.current;
    bulkTriggerRef.current = null;
    if (trigger && trigger.isConnected) trigger.focus();
    else listRef.current?.focus();
  }, [bulkAssignOpen]);

  // `groupByRegion` değişince de temizle (gruplama yapısı değişir).
  useEffect(() => {
    setSelectedOrderIds(new Set());
  }, [groupByRegion]);
  const [currentTime, setCurrentTime] = useState(() => new Date());

  // Yaş rozeti için saat enjekte edilir — her 30 sn güncellenir.
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 30_000);
    return () => clearInterval(interval);
  }, []);
  const labelLookup = usePanelLabelLookup();

  function changeView(next: OrdersView): void {
    setView(next);
    storeView(next);
  }

  function changeSource(next: SourceFilter): void {
    setSourceFilter(next);
    storeSource(next);
  }
  // Serbest tarih aralığı: seçim "custom" olduğunda başlangıç ve bitiş zorunlu. Başlangıç
  // bitişten büyükse istek atılmaz; hata metni form içinde gösterilir. Yalnız BİRİ doluysa
  // (örn. başlangıç var, bitiş yok) da istek atılmaz ve "iki tarihi de seçin" ipucu gösterilir
  // — yoksa sorgu sonsuza dek `isPending=true` ile döner ve "Yükleniyor…" yazısı ekranda
  // hata metniyle aynı anda dolaşırdı.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const dateRangeIncomplete = dateFilter === 'custom' && (dateFrom === '' || dateTo === '');
  const dateRangeReversed = dateFilter === 'custom' && !!dateFrom && !!dateTo && dateFrom > dateTo;
  const customRangeValid =
    dateFilter !== 'custom' || (!!dateFrom && !!dateTo && dateFrom <= dateTo);

  const vehicles = useVehicles();
  const orders = useOrders(
    {
      status: status === 'ALL' ? undefined : status,
      channel: channels.length > 0 ? channels.join(',') : undefined,
      source: sourceFilter === 'all' ? undefined : sourceFilter,
      vehicleId: vehicleId || undefined,
      assignment: assignment === 'all' ? undefined : assignment,
      dateFilter,
      from: dateFilter === 'custom' && dateFrom ? dateFrom : undefined,
      to: dateFilter === 'custom' && dateTo ? dateTo : undefined,
      q: search || undefined,
      sort,
      dir,
      pageSize: '100',
    },
    customRangeValid,
  );
  const detail = useOrder(selectedId);

  const activeFilters =
    (channels.length > 0 ? 1 : 0) +
    (vehicleId ? 1 : 0) +
    (search ? 1 : 0) +
    (dateFilter !== 'all' ? 1 : 0) +
    (assignment !== 'all' ? 1 : 0) +
    (sourceFilter !== 'all' ? 1 : 0);

  function toggleSort(column: 'receivedAt' | 'total' | 'status'): void {
    if (sort === column) setDir(dir === 'desc' ? 'asc' : 'desc');
    else {
      setSort(column);
      setDir('desc');
    }
  }

  function clearFilters(): void {
    setChannels([]);
    setVehicleId('');
    setSearch('');
    setDateFilter('all');
    setDateFrom('');
    setDateTo('');
    setAssignment('all');
    setSourceFilter('all');
  }

  const items = orders.data?.items ?? [];
  // Görünen id kümesinin imzası — uzunluk aynı kalıp içerik değişse de budama tetiklenir.
  const itemIdsKey = items.map((o) => o.id).join('|');
  // Gölge atama: kanal siparişlerinde panelin atadığı araç ↔ bizim kural (salt okunur).
  // Yalnız OWNER/MANAGER (canWrite) ve en çok 200 sipariş id'si; istek yoksa satır çizilmez.
  const shadowOrderIds = items
    .filter((o) => o.source === 'HAYAT_PANEL' || o.source === 'ABANT_PANEL')
    .slice(0, 200)
    .map((o) => o.id);
  const shadow = useShadowAssign({
    days: 30,
    orderIds: shadowOrderIds,
    enabled: canWrite,
    poll: false,
  });
  const shadowById = useMemo(
    () => new Map((shadow.data?.rows ?? []).map((r) => [r.orderId, r] as const)),
    [shadow.data],
  );

  // Görünen liste değişince seçimi buda — listeden çıkan sipariş seçili kalmaz.
  // `items` bu noktadan SONRA tanımlı (TS2448/2454 sırası).
  useEffect(() => {
    setSelectedOrderIds((prev) => {
      const visibleIds = new Set(itemIdsKey === '' ? [] : itemIdsKey.split('|'));
      const next = new Set([...prev].filter((id) => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [itemIdsKey]);

  /** Bölgenin önerilen aracı: önce sipariş yanıtındaki `region.defaultVehicleId`, yoksa bölge
   *  listesi. */
  function regionDefaultVehicle(regionId: string | null): string | null {
    if (!regionId) return null;
    const fromOrder = items.find((o) => o.region?.id === regionId)?.region?.defaultVehicleId;
    if (fromOrder) return fromOrder;
    return regions.data?.items?.find((r) => r.id === regionId)?.defaultVehicleId ?? null;
  }

  /** Atlananlar listesinde ham id yerine satırın gösterdiği etiket (müşteri adı · saat). */
  const orderLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const o of items) map[o.id] = `${o.customerName} · ${timeLabel(o.receivedAt)}`;
    return map;
  }, [items]);

  // Başlık hücreleri iki yerleşimde (gruplu/gruplamasız) aynıdır — tek kaynak.
  const headerCells = (
    <>
      <div className="th-cell">
        <button
          onClick={() => toggleSort('receivedAt')}
          style={{
            color: sort === 'receivedAt' ? 'var(--primary)' : 'inherit',
            font: 'inherit',
            textAlign: 'left',
          }}
        >
          {t('panel.colTime')} {sort === 'receivedAt' ? (dir === 'desc' ? '↓' : '↑') : ''}
        </button>
        {resizer(0)}
      </div>
      <div className="th-cell">
        <span>{t('panel.colChannel')}</span>
        {resizer(1)}
      </div>
      <div className="th-cell">
        <span>{t('panel.colCustomer')}</span>
        {resizer(2)}
      </div>
      {view === 'advanced' && (
        <div className="th-cell col-hide-sm">
          <span>{t('panel.colAddress')}</span>
          {resizer(3)}
        </div>
      )}
      <div className="th-cell col-hide-sm">
        <span>{t('panel.colItems')}</span>
        {resizer(view === 'simple' ? 3 : 4)}
      </div>
      <div className="th-cell">
        <button
          onClick={() => toggleSort('total')}
          style={{
            color: sort === 'total' ? 'var(--primary)' : 'inherit',
            font: 'inherit',
            textAlign: 'right',
          }}
        >
          {t('panel.colTotal')} {sort === 'total' ? (dir === 'desc' ? '↓' : '↑') : ''}
        </button>
        {resizer(view === 'simple' ? 4 : 5)}
      </div>
      <div className="th-cell">
        <button
          onClick={() => toggleSort('status')}
          style={{
            color: sort === 'status' ? 'var(--primary)' : 'inherit',
            font: 'inherit',
            textAlign: 'left',
          }}
        >
          {t('panel.colStatus')} {sort === 'status' ? (dir === 'desc' ? '↓' : '↑') : ''}
        </button>
        {resizer(view === 'simple' ? 5 : 6)}
      </div>
      <div className="th-cell col-hide-sm">
        <span>{t('panel.colVehicle')}</span>
        {resizer(view === 'simple' ? 6 : 7)}
      </div>
      {view === 'advanced' && (
        <div className="th-cell col-hide-sm">
          <span>{t('hayatPush.ui.statusLabel')}</span>
          {resizer(8)}
        </div>
      )}
    </>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100%' }}>
      <div style={{ flex: 1, padding: '24px 32px 32px', minWidth: 0 }}>
        <div className="orders-tabbar no-print">
          <div className="orders-tabbar-scroll">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => setStatus(tab)}
                aria-pressed={status === tab}
                style={{
                  padding: '12px 14px',
                  borderBottom: `2px solid ${status === tab ? 'var(--primary)' : 'transparent'}`,
                  color: status === tab ? 'var(--text-primary)' : 'var(--text-secondary)',
                  fontWeight: status === tab ? 600 : 500,
                  fontSize: 14.5,
                }}
              >
                {tab === 'ALL' ? t('panel.filterAll') : t(`orderStatus.${tab}`)}
              </button>
            ))}
          </div>
          {canWrite && (
            <button className="btn btn-primary orders-new-btn" onClick={() => setModalOpen(true)}>
              {t('panel.newOrder')}
            </button>
          )}
        </div>

        <div
          className="no-print"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="filter-group-label">{t('panelUi.filterChannelLabel')}</span>
            <div style={{ display: 'flex', gap: 6 }}>
              {CHANNELS.map((s) => {
                const active = channels.includes(s);
                return (
                  <button
                    key={s}
                    onClick={() =>
                      setChannels(active ? channels.filter((x) => x !== s) : [...channels, s])
                    }
                    aria-pressed={active}
                    style={{
                      height: 38,
                      padding: '0 13px',
                      borderRadius: 'var(--r-input)',
                      border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                      background: active ? 'var(--primary-bg)' : 'var(--surface)',
                      color: active ? 'var(--primary)' : 'var(--text-secondary)',
                      fontWeight: 500,
                      fontSize: 13,
                    }}
                  >
                    {t(`orderChannel.${s}`)}
                  </button>
                );
              })}
            </div>
          </div>

          <span aria-hidden style={{ width: 1, height: 24, background: 'var(--border)' }} />

          {/* Kaynak süzgeci: patron "Hayat'tan gelenleri göster" demek için (§3.6). Kanal
              süzgecinden ayrı tutulur — kanal müşterinin verdiği yer, kaynak panelin
              çektiği yer. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="filter-group-label">{t('panelUi.filterSourceLabel')}</span>
            <div style={{ display: 'flex', gap: 6 }} data-testid="orders-source-filter">
              {SOURCE_CHIPS.map((chip) => {
                const active = sourceFilter === chip.value;
                return (
                  <button
                    key={chip.value}
                    onClick={() => changeSource(chip.value)}
                    aria-pressed={active}
                    data-testid={chip.testId}
                    style={{
                      height: 38,
                      padding: '0 13px',
                      borderRadius: 'var(--r-input)',
                      border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                      background: active ? 'var(--primary-bg)' : 'var(--surface)',
                      color: active ? 'var(--primary)' : 'var(--text-secondary)',
                      fontWeight: 500,
                      fontSize: 13,
                    }}
                  >
                    {t(chip.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span id="orders-date-label" className="filter-group-label">
              {t('orders.filterDate')}
            </span>
            <div
              role="group"
              aria-labelledby="orders-date-label"
              style={{
                display: 'flex',
                background: 'var(--page-bg)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-pill)',
                padding: 3,
              }}
            >
              {DATE_FILTERS.map((d) => (
                <button
                  key={d.value}
                  onClick={() => setDateFilter(d.value)}
                  aria-pressed={dateFilter === d.value}
                  style={{
                    borderRadius: 'var(--r-pill)',
                    padding: '7px 13px',
                    background: dateFilter === d.value ? 'var(--primary)' : 'transparent',
                    color: dateFilter === d.value ? '#fff' : 'var(--text-secondary)',
                    fontWeight: 600,
                    fontSize: 12.5,
                  }}
                >
                  {periodLabel(d.period)}
                </button>
              ))}
            </div>
          </div>

          {dateFilter === 'custom' && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-input)',
                padding: '4px 10px',
                height: 38,
              }}
            >
              <label
                style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 4 }}
              >
                {t('panel.dateFrom')}
                <input
                  type="date"
                  data-testid="orders-date-from"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{ border: 'none', background: 'transparent', font: 'inherit', height: 28 }}
                />
              </label>
              <span style={{ color: 'var(--text-secondary)' }}>–</span>
              <label
                style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 4 }}
              >
                {t('panel.dateTo')}
                <input
                  type="date"
                  data-testid="orders-date-to"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{ border: 'none', background: 'transparent', font: 'inherit', height: 28 }}
                />
              </label>
            </div>
          )}
          {dateRangeIncomplete && (
            <p
              data-testid="orders-date-range-incomplete"
              style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0, width: '100%' }}
            >
              {t('panel.dateRangeIncomplete')}
            </p>
          )}
          {dateRangeReversed && (
            <p
              data-testid="orders-date-range-error"
              style={{ fontSize: 12.5, color: 'var(--danger)', margin: 0, width: '100%' }}
            >
              {t('panel.dateRangeInvalid')}
            </p>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label htmlFor="orders-vehicle" className="filter-group-label">
              {t('orders.filterVehicle')}
            </label>
            <select
              id="orders-vehicle"
              className="input"
              style={{ width: 190, height: 38 }}
              value={vehicleId ? vehicleId : assignment === 'unassigned' ? 'unassigned' : ''}
              onChange={(e) => {
                if (e.target.value === 'unassigned') {
                  setAssignment('unassigned');
                  setVehicleId('');
                } else {
                  setAssignment('all');
                  setVehicleId(e.target.value);
                }
              }}
            >
              <option value="">{t('panel.allVehicles')}</option>
              <option value="unassigned">{t('panel.unassigned')}</option>
              {(vehicles.data?.items ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plate}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label htmlFor="orders-search" className="filter-group-label">
              {t('orders.filterSearch')}
            </label>
            <input
              id="orders-search"
              className="input"
              style={{ width: 280, height: 38 }}
              placeholder={t('panel.searchOrders')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <span style={{ flex: 1 }} />
          {activeFilters > 0 && (
            <button
              className="btn"
              style={{ color: 'var(--primary)', fontSize: 13, padding: 0 }}
              onClick={clearFilters}
            >
              {t('panel.clearFilters')}
            </button>
          )}
          {/* Sade/Gelişmiş anahtarı: tarih çipleriyle KARIŞMASIN diye dolgu pill değil, ince
              kenarlıklı ghost segment. Tablo başlığıyla aynı satıra konmaz: esnek sütunlar
              başlıkta daralır, satırlarda daralmaz ve başlıklar sütunlarından kayar. */}
          <div
            className="orders-view-switch"
            role="group"
            aria-label={t('ordersView.groupLabel')}
            style={{
              display: 'inline-flex',
              flexShrink: 0,
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-input)',
              overflow: 'hidden',
            }}
          >
            <button
              onClick={() => changeView('simple')}
              aria-pressed={view === 'simple'}
              data-testid="orders-view-simple"
              style={{
                padding: '9px 14px',
                borderRight: '1px solid var(--border)',
                background: view === 'simple' ? 'var(--page-bg)' : 'var(--surface)',
                color: view === 'simple' ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: view === 'simple' ? 700 : 500,
                fontSize: 12.5,
              }}
            >
              {t('ordersView.simple')}
            </button>
            <button
              onClick={() => changeView('advanced')}
              aria-pressed={view === 'advanced'}
              data-testid="orders-view-advanced"
              style={{
                padding: '9px 14px',
                background: view === 'advanced' ? 'var(--page-bg)' : 'var(--surface)',
                color: view === 'advanced' ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: view === 'advanced' ? 700 : 500,
                fontSize: 12.5,
              }}
            >
              {t('ordersView.advanced')}
            </button>
          </div>

          {/* Bölge gruplama ve toplu atama — `groupByRegion` açıkken gösterilir. */}
          {canWrite && (
            <button
              onClick={() => {
                setGroupByRegion((v) => !v);
                setSelectedOrderIds(new Set());
              }}
              aria-pressed={groupByRegion}
              data-testid="orders-group-by-region"
              style={{
                height: 38,
                padding: '0 13px',
                borderRadius: 'var(--r-input)',
                border: `1px solid ${groupByRegion ? 'var(--primary)' : 'var(--border)'}`,
                background: groupByRegion ? 'var(--primary-bg)' : 'var(--surface)',
                color: groupByRegion ? 'var(--primary)' : 'var(--text-secondary)',
                fontWeight: 500,
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              {t(groupByRegion ? 'panel.orders.groupByRegionActive' : 'panel.orders.groupByRegion')}
            </button>
          )}

          {groupByRegion && selectedOrderIds.size > 0 && (
            <button
              className="btn btn-primary"
              onClick={(e) => {
                // Yalnız GÖRÜNEN seçili satırlar gider; hepsi aynı bölgedeyse öneri o bölgenin aracı.
                const selectedItems = items.filter((o) => selectedOrderIds.has(o.id));
                const firstRegion = selectedItems[0]?.region?.id ?? null;
                const sameRegion = selectedItems.every((o) => o.region?.id === firstRegion);
                openBulkAssign(
                  selectedItems.map((o) => o.id),
                  sameRegion ? regionDefaultVehicle(firstRegion) : null,
                  e.currentTarget,
                );
              }}
              data-testid="orders-bulk-assign-btn"
              style={{ height: 38, padding: '0 14px', fontSize: 13, flexShrink: 0 }}
            >
              {t('panel.orders.bulkAssign')} ({selectedOrderIds.size})
            </button>
          )}
        </div>

        <div
          className="card"
          ref={listRef}
          tabIndex={-1}
          data-testid="orders-list"
          style={{ overflow: 'auto', outline: 'none' }}
        >
          {/* Gruplamasız: başlık ilk sürümle birebir aynı (grid elemanı useColumnWidths stilini
              ve headerRef'i taşır). Grup modunda checkbox sütunu grid DIŞINDA, satırlardakiyle
              aynı sabit genişlikte (CHECK_COL_WIDTH); grid yine stili ve ref'i taşır. */}
          {groupByRegion ? (
            <div style={{ display: 'flex', alignItems: 'center', background: 'var(--page-bg)' }}>
              <div style={checkColStyle}>
                <input
                  type="checkbox"
                  aria-label={t('panel.selectAll')}
                  checked={selectedOrderIds.size === items.length && items.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedOrderIds(new Set(items.map((o) => o.id)));
                    } else {
                      setSelectedOrderIds(new Set());
                    }
                  }}
                  data-testid="orders-select-all"
                />
              </div>
              <div
                ref={headerRef}
                className={`table-head ${gridClassName(view)}`}
                style={{ flex: 1, padding: '0 22px', height: 44, ...style }}
              >
                {headerCells}
              </div>
            </div>
          ) : (
            <div
              ref={headerRef}
              className={`table-head ${gridClassName(view)}`}
              style={{ padding: '0 22px', height: 44, ...style }}
            >
              {headerCells}
            </div>
          )}

          {/* Tarih aralığı seçildiyse ya eksik ya hatalıysa sorgu `enabled=false` ile
              askıya alınır; `isPending` true döner. Yükleme göstergesi bu durumda hata
              metniyle aynı anda dönüyor ve kullanıcı iki yönlü bir bekleme/hata görüntüsü
              alıyordu. İkinci koşul: tarih aralığı seçili ve (eksik | hatalı) ise
              "boş tablo" gösterilir; yükleme göstergesi yerine sadece hata/ipucu metni kalır. */}
          {!customRangeValid ? (
            <div
              style={{ padding: '52px 24px', textAlign: 'center', color: 'var(--text-secondary)' }}
            >
              <Icon name="funnel" size={56} />
            </div>
          ) : orders.isPending ? (
            <p
              style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-secondary)' }}
            >
              {t('common.loading')}
            </p>
          ) : orders.isError ? (
            <div
              style={{ padding: '52px 24px', textAlign: 'center', color: 'var(--text-secondary)' }}
            >
              <p style={{ margin: '14px 0', fontWeight: 500 }}>{t('orders.loadFailed')}</p>
              <button className="btn btn-secondary" onClick={() => void orders.refetch()}>
                {t('panel.retry')}
              </button>
            </div>
          ) : items.length === 0 ? (
            <div
              style={{ padding: '52px 24px', textAlign: 'center', color: 'var(--text-secondary)' }}
            >
              <Icon name="funnel" size={56} />
              <p style={{ margin: '14px 0', fontWeight: 500 }}>{t('panel.noOrdersFound')}</p>
              {activeFilters > 0 && (
                <button className="btn btn-primary" onClick={clearFilters}>
                  {t('panel.clearFilters')}
                </button>
              )}
            </div>
          ) : groupByRegion ? (
            <RegionGroupedList
              items={items}
              currentTime={currentTime}
              selectedOrderIds={selectedOrderIds}
              setSelectedOrderIds={setSelectedOrderIds}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              style={style}
              view={view}
              labelLookup={labelLookup}
              shadowById={shadowById}
              onBulkAssign={(regionId, orderIds, trigger) =>
                openBulkAssign(orderIds, regionDefaultVehicle(regionId), trigger)
              }
            />
          ) : (
            <ul
              className="rowgrid-stack-list"
              style={{ listStyle: 'none', minWidth: view === 'simple' ? 960 : 1330 }}
            >
              {items.map((order) => (
                <OrderRow
                  key={order.id}
                  order={order}
                  currentTime={currentTime}
                  selectedId={selectedId}
                  setSelectedId={setSelectedId}
                  style={style}
                  view={view}
                  labelLookup={labelLookup}
                  shadowById={shadowById}
                />
              ))}
            </ul>
          )}

          {orders.data && items.length > 0 && (
            <p
              style={{
                padding: '14px 22px',
                fontSize: 13,
                color: 'var(--text-secondary)',
                borderTop: '1px solid var(--border)',
              }}
            >
              {activeFilters > 0
                ? t('panelUi.filteredShowing', {
                    n: activeFilters,
                    shown: items.length,
                    total: orders.data.total,
                  })
                : t('panelUi.showingCount', { shown: items.length, total: orders.data.total })}
            </p>
          )}
        </div>
      </div>

      {selectedId && detail.data && (
        <OrderDetail order={detail.data} canWrite={canWrite} onClose={() => setSelectedId(null)} />
      )}
      {modalOpen && <NewOrderModal onClose={() => setModalOpen(false)} />}
      {bulkAssignOpen && (
        <BulkAssignDialog
          orderIds={bulkOrderIds}
          orderLabels={orderLabels}
          suggestedVehicleId={suggestedVehicleId}
          onClose={closeBulkAssign}
        />
      )}
    </div>
  );
}

function timeLabel(iso: string): string {
  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/** Bugün değilse saatin altındaki gün: "Dün" ya da `GG.AA` (başka yılsa `GG.AA.YYYY`).
 *  "Tümü"/"Bu hafta" listesinde 3 hafta önceki sipariş de yalnız "23:13" görünüyordu. */
export function dayHint(iso: string, now: Date = new Date()): string | null {
  const at = new Date(iso);
  const day = istanbulDayString(at);
  if (day === istanbulDayString(now)) return null;
  if (day === istanbulDayString(new Date(now.getTime() - 24 * 3600 * 1000))) {
    return t('panel.dateYesterday');
  }
  const full = formatDate(at);
  return day.slice(0, 4) === istanbulDayString(now).slice(0, 4) ? full.slice(0, 5) : full;
}

/** Hücre sayısı görünüme göre değişir (7/9); CSS sınıfı bunlardan AYRI tanımlanır — ikisi
 *  buradan ayrışırsa başlık ve satır tekrar birbirinden kayar (bkz. base.css .orders-grid--*). */
function gridClassName(view: OrdersView): string {
  return view === 'simple' ? 'orders-grid--simple' : 'orders-grid--advanced';
}

/** Grup modunda checkbox sütunu: grid DIŞINDA, başlıkta ve satırda aynı sabit genişlik. */
const CHECK_COL_WIDTH = 44;
const checkColStyle: React.CSSProperties = {
  width: CHECK_COL_WIDTH,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/**
 * Araç hücresi (Sade + Gelişmiş ortak): TEK satır "Plaka · Personel adı". Panel etiketi eşleşmemişse
 * çıplak numara yerine okunur metin ("Bölge: Üst" / "Personel eşlenmemiş") + küçük "Panel Atama'dan
 * eşle" ipucu. "Atanamadı" gibi gerekçe rozetleri satırın akışını bozmasın diye metnin yanına sarılır.
 */
function VehicleCell({ order, labelLookup }: { order: Order; labelLookup: PanelLabelLookup }) {
  const label = resolveVehicleLabel(order, labelLookup);
  const muted = label.kind === 'unmapped' || label.kind === 'region';
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      <span
        data-testid={`order-vehicle-text-${order.id}`}
        style={{
          fontWeight: muted ? 400 : 600,
          fontSize: muted ? 12.5 : 13,
          color: muted ? 'var(--text-secondary)' : undefined,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label.text}
      </span>
      {label.needsMapping ? (
        <span style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>
          {t('orders.vehicleLabel.mapHint')}
        </span>
      ) : null}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Yaş rozeti                                                           */
/* ------------------------------------------------------------------ */

/** Dakika cinsinden yaş; negativesiz, minimum 0. */
function pendingAgeMinutes(receivedAt: string, now: Date): number {
  const ms = now.getTime() - new Date(receivedAt).getTime();
  return Math.max(0, Math.floor(ms / 60_000));
}

function AgeBadge({
  receivedAt,
  currentTime,
  order,
}: {
  receivedAt: string;
  currentTime: Date;
  order: Order;
}) {
  // Yalnız bekleyen (NEW/CONFIRMED) ve atanmamış siparişlerde gösterilir.
  if (order.status !== 'NEW' && order.status !== 'CONFIRMED') return null;
  if (order.vehicle?.id) return null;

  const age = pendingAgeMinutes(receivedAt, currentTime);

  let color: string;
  let bg: string;
  let text: string;
  if (age < 15) {
    color = 'var(--text-secondary)';
    bg = '#F3F4F6';
    text =
      age === 0
        ? t('panel.orders.pendingAge.now')
        : t('panel.orders.pendingAge.minutes', { n: age });
  } else if (age < 30) {
    color = '#92400E';
    bg = '#FEF3C7';
    text = t('panel.orders.pendingAge.minutes', { n: age });
  } else {
    color = '#991B1B';
    bg = '#FEE2E2';
    text =
      age < 60
        ? t('panel.orders.pendingAge.minutes', { n: age })
        : t('panel.orders.pendingAge.hours', { n: Math.floor(age / 60) });
  }
  return (
    <span
      data-testid="pending-age-badge"
      style={{
        display: 'table',
        marginTop: 2,
        padding: '1px 6px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.3,
        color,
        background: bg,
      }}
    >
      {text}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Bölge gruplu liste                                                  */
/* ------------------------------------------------------------------ */

function RegionGroupedList({
  items,
  currentTime,
  selectedOrderIds,
  setSelectedOrderIds,
  selectedId,
  setSelectedId,
  style,
  view,
  labelLookup,
  shadowById,
  onBulkAssign,
}: {
  items: Order[];
  currentTime: Date;
  selectedOrderIds: Set<string>;
  setSelectedOrderIds: (next: Set<string>) => void;
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  style?: React.CSSProperties;
  view: OrdersView;
  labelLookup: PanelLabelLookup;
  /** Grup düğmesi: o grubun sipariş id'leri + tetikleyici düğme (kapanışta odak dönüşü). */
  shadowById: ReadonlyMap<string, ShadowRow>;
  onBulkAssign: (regionId: string, orderIds: string[], trigger: HTMLElement) => void;
}) {
  // Bölgelere göre grupla — region null en sona.
  const groups = useMemo(() => {
    const map = new Map<string | null, Order[]>();
    for (const o of items) {
      const key = o.region?.id ?? null;
      const list = map.get(key) ?? [];
      list.push(o);
      map.set(key, list);
    }
    const result: { regionId: string | null; regionName: string | null; orders: Order[] }[] = [];
    for (const [key, orders] of map) {
      result.push({
        regionId: key,
        regionName: key ? (items.find((o) => o.region?.id === key)?.region?.name ?? key) : null,
        orders,
      });
    }
    // null en sona.
    result.sort((a, b) => {
      if (a.regionId === null) return 1;
      if (b.regionId === null) return -1;
      return (a.regionName ?? '').localeCompare(b.regionName ?? '');
    });
    return result;
  }, [items]);

  return (
    <div style={{ minWidth: 600 }}>
      {groups.map(({ regionId, regionName, orders }) => (
        <div key={regionId ?? 'no-region'}>
          {/* Grup başlığı */}
          <div
            data-testid="region-group-header"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 22px',
              background: 'var(--page-bg)',
              borderBottom: '1px solid var(--border)',
              borderTop: '1px solid var(--border)',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <input
              type="checkbox"
              aria-label={t('panel.selectAll')}
              checked={
                orders[0] != null &&
                selectedOrderIds.has(orders[0].id) &&
                orders.every((o) => selectedOrderIds.has(o.id))
              }
              onChange={(e) => {
                if (e.target.checked) {
                  const next = new Set(selectedOrderIds);
                  orders.forEach((o) => next.add(o.id));
                  setSelectedOrderIds(next);
                } else {
                  const next = new Set(selectedOrderIds);
                  orders.forEach((o) => next.delete(o.id));
                  setSelectedOrderIds(next);
                }
              }}
              data-testid={`region-select-${regionId ?? 'no-region'}`}
            />
            <span style={{ fontWeight: 600, fontSize: 13 }}>
              {regionName ?? t('panel.orders.groupNone')}
            </span>
            <span
              style={{
                fontSize: 12,
                color: 'var(--text-secondary)',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '0 7px',
              }}
            >
              {orders.length}
            </span>
            {regionId !== null && orders.length > 0 && (
              <button
                className="btn"
                data-testid={`region-bulk-assign-${regionId}`}
                style={{
                  marginLeft: 'auto',
                  height: 26,
                  padding: '0 10px',
                  fontSize: 12,
                  color: 'var(--primary)',
                  flexShrink: 0,
                }}
                onClick={(e) =>
                  onBulkAssign(
                    regionId,
                    orders.map((o) => o.id),
                    e.currentTarget,
                  )
                }
              >
                {t('panel.orders.bulkAssign')}
              </button>
            )}
          </div>

          {/* Sipariş satırları */}
          {orders.map((order) => (
            <OrderRow
              key={order.id}
              order={order}
              currentTime={currentTime}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              style={style}
              view={view}
              showCheckbox
              checked={selectedOrderIds.has(order.id)}
              onCheck={(checked) => {
                const next = new Set(selectedOrderIds);
                if (checked) next.add(order.id);
                else next.delete(order.id);
                setSelectedOrderIds(next);
              }}
              labelLookup={labelLookup}
              shadowById={shadowById}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sipariş satırı (ortak)                                              */
/* ------------------------------------------------------------------ */

/**
 * Gölge atama satırı (salt okunur): yalnız panelin atadığı araç bizim bölge kuralından FARKLIYSA
 * tek küçük uyarı satırı ("Bölge kuralı: Zeynep Kaya · Farklı"). Uyuşan ya da karşılaştırılamayan
 * satırda hiçbir şey çizilmez; panel etiketi (çalışan no) hiçbir zaman basılmaz.
 */
function ShadowLine({ row }: { row: ShadowRow | undefined }) {
  if (!row || row.agree !== false) return null;
  const ours = plateAndPerson(row.ours.vehicle?.plate ?? null, row.ours.person?.name ?? null);
  if (!ours) return null;
  return (
    <span
      data-testid={`shadow-line-${row.orderId}`}
      data-agree="false"
      style={{
        display: 'block',
        fontSize: 11,
        color: 'var(--warning)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {t('orders.shadow.differs', { ours })}
      <span
        data-testid={`shadow-badge-${row.orderId}`}
        style={{ marginLeft: 6, fontWeight: 600, color: 'var(--text-primary)' }}
      >
        {t('panel.shadowAssign.agreeDiff')}
      </span>
    </span>
  );
}

interface OrderRowProps {
  order: Order;
  currentTime: Date;
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  style?: React.CSSProperties;
  view: 'simple' | 'advanced';
  showCheckbox?: boolean;
  checked?: boolean;
  onCheck?: (checked: boolean) => void;
  labelLookup: PanelLabelLookup;
  shadowById: ReadonlyMap<string, ShadowRow>;
}

function OrderRow({
  order,
  currentTime,
  selectedId,
  setSelectedId,
  style,
  view,
  showCheckbox,
  checked,
  onCheck,
  labelLookup,
  shadowById,
}: OrderRowProps) {
  const hint = dayHint(order.receivedAt, currentTime);
  // Gruplamasız satır ilk sürümle birebir: <li><button grid>. Grup modunda checkbox sütunu
  // grid DIŞINDA, başlıktaki ile aynı sabit genişlikte (CHECK_COL_WIDTH).
  return (
    <li
      key={order.id}
      style={
        showCheckbox
          ? {
              display: 'flex',
              alignItems: 'stretch',
              borderTop: '1px solid var(--border)',
              height: 58,
            }
          : undefined
      }
    >
      {showCheckbox && (
        <label style={{ ...checkColStyle, cursor: 'pointer' }}>
          <input
            type="checkbox"
            aria-label={t('panel.select')}
            checked={checked ?? false}
            onChange={(e) => {
              e.stopPropagation();
              onCheck?.(e.target.checked);
            }}
            style={{ width: 16, height: 16, cursor: 'pointer' }}
            data-testid={`order-check-${order.id}`}
          />
        </label>
      )}
      <button
        onClick={() => setSelectedId(order.id)}
        className={gridClassName(view)}
        style={{
          ...(showCheckbox
            ? { flex: 1, height: '100%' }
            : { width: '100%', height: 58, borderTop: '1px solid var(--border)' }),
          padding: '0 22px',
          background: selectedId === order.id ? 'var(--primary-bg)' : 'transparent',
          textAlign: 'left',
          font: 'inherit',
          ...style,
        }}
      >
        <span style={{ fontWeight: 500, fontSize: 13.5, color: 'var(--text-secondary)' }}>
          {timeLabel(order.receivedAt)}
          {hint && <span style={{ display: 'block', fontSize: 11, fontWeight: 400 }}>{hint}</span>}
          <AgeBadge receivedAt={order.receivedAt} currentTime={currentTime} order={order} />
        </span>
        <ChannelPill channel={order.channel} source={order.source} compact />
        <span style={{ overflow: 'hidden' }}>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: 500,
              fontSize: 14,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {order.customerName}
            </span>
            {orderNoteSummary(order) ? (
              <span
                aria-label={t('panelFields.customerNote')}
                data-testid={`orders-note-${order.id}`}
                title={orderNoteSummary(order) ?? undefined}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 16,
                  height: 16,
                  borderRadius: 'var(--r-pill)',
                  background: 'var(--bg-warning-soft)',
                  color: 'var(--warning)',
                  flexShrink: 0,
                }}
              >
                <Icon name="info" size={11} />
              </span>
            ) : null}
            <RegionMatchBadge order={order} hideExact />
          </span>
          {order.customerPhone && (
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              {order.customerPhone}
            </span>
          )}
        </span>
        {view === 'advanced' && (
          <span
            className="col-hide-sm"
            style={{
              fontSize: 13.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {order.address}
          </span>
        )}
        <span
          className="col-hide-sm"
          style={{
            fontWeight: 500,
            fontSize: 13.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {itemsSummary(order.items)}
        </span>
        <span style={{ fontWeight: 600, fontSize: 13.5, textAlign: 'right' }}>
          {formatMoney(orderAmount(order))}
        </span>
        <OrderStatusCell order={order} />
        <span className="col-hide-sm" data-testid={`order-vehicle-${order.id}`}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <VehicleCell order={order} labelLookup={labelLookup} />
            <AssignReasonBadge order={order} />
          </span>
          <ShadowLine row={shadowById.get(order.id)} />
        </span>
        {view === 'advanced' && (
          <span className="col-hide-sm" data-testid={`hayat-status-row-${order.id}`}>
            {order.source === 'HAYAT_PANEL' || order.source === 'ABANT_PANEL' ? (
              <PanelStagePill source={order.source} raw={order.externalStatusRaw} />
            ) : (
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>—</span>
            )}
          </span>
        )}
      </button>
    </li>
  );
}
